<template>
    <div class="treeselect" ref="treeselectWrapper" :class="{ 'treeselect--disabled': disabled, 'treeselect--open': menuIsOpen, 'treeselect--always-open': alwaysOpen, 'theme-light': theme === 'light', 'theme-dark': theme === 'dark' }" tabindex="0" @focusin="handleFocusIn" @focusout="handleFocusOut">
        <div :class="{ 'treeselect-prepend-group': prependIcon }">
            <input v-if="name" type="hidden" :name="name" :value="joinedValue"/>
            <b-input-group-prepend v-if="prependIcon" is-text>
                <i :class="prependIcon"></i>
            </b-input-group-prepend>
            <div class="treeselect-control" ref="control" :tabindex="computedTabIndex">
                <b-input-group>
                    <slot name="selected-pill">
                        <b-input-group-prepend v-if="!hidePill">
                            <template v-if="multiple">
                                <slot name="multiple-selection" v-bind="{selection: selectedNodes}">
                                    <span v-for="n in selectedNodes" :key="n[trackBy]" class="treeselect-value-pill" :class="[variant]">
                                        <span class="treeselect-value-pill-label">
                                            <slot name="selected-pill-label" :node="n">
                                                {{ n[label] }}
                                            </slot>
                                        </span>
                                        <button v-if="!disabled" class="treeselect-value-pill-remove" @click.stop="uncheckSingleNode(n)">
                                            <i class="fa-solid fa-xmark fa-xs"/>
                                        </button>
                                    </span>
                                </slot>
                            </template>
                            <template v-else-if="selectedNodes[0]">
                                <slot name="simple-selection" v-bind="{selection: selectedNodes[0]}">
                                    <span class="treeselect-value-pill">
                                        {{ selectedNodes[0][label] }}
                                        <button v-if="clearable && !disabled" class="treeselect-value-single-remove" @click.stop="clearValue">
                                            <i class="fa-solid fa-xmark"></i>
                                        </button>
                                    </span>
                                </slot>
                            </template>
                            <div v-if="showAllSelectedPill && !selectedNodes.length" class="treeselect-value-pill all-selected">
                                <span class="treeselect-value-pill-label py-1 pr-2" v-tr>ALL|TOUS</span>
                            </div>
                        </b-input-group-prepend>
                    </slot>
                    <div class="treeselect-search-input-wrapper">
                        <b-input v-if="searchable && !disabled" ref="searchInput" autocomplete="off" class="treeselect-search-input" v-model="searchQuery" :placeholder="placeholder" :debounce="searchDebounce" @keydown.stop="handleKeydown"/>
                        <span v-else class="treeselect-search-input--fake"></span>
                        <b-input-group-append>
                            <button v-if="clearable && multiple && selectedNodes.length && !disabled" class="treeselect-clear-all" @click.stop="clearAll">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                            <button class="treeselect-arrow" @click="handleCaretClick" @focusin.prevent.stop>
                                <i v-if="menuIsOpen" class="fa-solid fa-caret-up"></i>
                                <i v-else class="fa-solid fa-caret-down"></i>
                            </button>
                        </b-input-group-append>
                    </div>
                </b-input-group>
            </div>
        </div>
        <div class="treeselect-menu-container"
             v-show="menuIsOpen || alwaysOpen"
             :style="{ zIndex, maxHeight: maxHeight + 'px' }"
             ref="menu"
             @scroll="onVirtualScroll">
            <div class="treeselect-before-list">
                <slot name="before-list"/>
            </div>
            <div class="treeselect-loading" v-if="menuLoading">
                <slot name="loading">
                    <i class="fa-solid fa-spinner-scale fa-spin-pulse fa-fw"></i>
                    <span v-if="loadingText" class="ml-2">{{ loadingText }}</span>
                </slot>
            </div>
            <div class="treeselect-no-options" v-else-if="!rootOptions || rootOptions.length === 0">
                <span v-if="async && !searchQuery">Type to search...</span>
                <span v-else>{{ noOptionsText }}</span>
            </div>
            <div class="treeselect-no-results" v-else-if="filteredRootOptions.length === 0">
                <span>{{ noResultsText }}</span>
            </div>
            <ul class="treeselect-menu">
                <!-- Spacer for items above the visible slice -->
                <li v-if="topSpacerHeight" :style="{ height: topSpacerHeight + 'px' }"></li>
                <!-- Render only the visible slice of options -->
                <e-treeselect-option
                    v-for="rootNode in visibleRootOptions"
                    :key="rootNode[trackBy]"
                    :track-by="trackBy"
                    :node="rootNode"
                    :level="0"
                    :multiple="multiple"
                    :flat="flat"
                    :disable-branch-nodes="disableBranchNodes"
                    :hide-branch-checkboxes="hideBranchCheckboxes"
                    :value-consists-of="valueConsistsOf"
                    :checked-ids="checkedState.checkedIds"
                    :indeterminate-ids="checkedState.indeterminateIds"
                    :expanded-ids="expandedState"
                    :label="label"
                    :search-query="searchQuery"
                    :search-nested="searchNested"
                    :disable-fuzzy-matching="disableFuzzyMatching"
                    :show-count="showCount"
                    :load-options="loadData"
                    :instance-id="instanceId"
                    :highlighted-node-id="highlightedNodeId"
                    :emitEvent="emitEvent"
                    :onCheck="handleCheck"
                    :onExpand="toggleExpanded"
                    :onLoadChildren="handleLoadChildren"
                    :default-expand-level="defaultExpandLevel"
                >
                    <template v-slot:option-label="slotProps">
                        <slot name="option-label" v-bind="slotProps"/>
                    </template>
                </e-treeselect-option>
                <!-- Spacer for items below the visible slice -->
                <li v-if="bottomSpacerHeight" :style="{ height: bottomSpacerHeight + 'px' }"></li>
            </ul>
            <div class="treeselect-after-list">
                <slot name="after-list"/>
            </div>
            <div v-if="async" class="treeselect-loading mb-2" :class="{ 'invisible' : !scrollLoading }">
                <slot name="loading">
                    <i class="fa-solid fa-spinner-scale fa-spin-pulse fa-fw"></i>
                    <span v-if="loadingText" class="ml-2">{{ loadingText }}</span>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import ETreeselectOption from "./e-treeselect-option.vue"

export default {
    name: `e-treeselect`,
    components: {ETreeselectOption},
    props: {
        options: {type: Array, default: () => []},
        value: {type: [Array, String, Number, Object, null], default: null},
        trackBy: {type: String, default: `id`},
        multiple: {type: Boolean, default: false},
        disableBranchNodes: {type: Boolean, default: false},
        hideBranchCheckboxes: {type: Boolean, default: false},
        searchable: {type: Boolean, default: true},
        searchDebounce: {type: Number, default: 300},
        label: {type: String, default: `label`},
        disableFuzzyMatching: {type: Boolean, default: false},
        searchNested: {type: Boolean, default: false},
        fetchItems: {type: Function, default: null},
        async: {type: Boolean, default: false},
        flat: {type: Boolean, default: false},
        virtualScroll: {type: Boolean, default: false},
        valueConsistsOf: {type: String, default: `BRANCH_PRIORITY`, validator: value => [`ALL`, `BRANCH_PRIORITY`, `LEAF_PRIORITY`, `ALL_WITH_INDETERMINATE`].includes(value)},
        placeholder: {type: String, default: `Type to search...`},
        prependIcon: {type: String, default: null},
        clearable: {type: Boolean, default: true},
        disabled: {type: Boolean, default: false},
        alwaysOpen: {type: Boolean, default: false},
        openOnClick: {type: Boolean, default: true},
        closeOnSelect: {type: Boolean, default: false},
        showCount: {type: Boolean, default: false},
        hidePill: {type: Boolean, default: false},
        showAllSelectedPill: {type: Boolean, default: false},
        name: {type: String, default: ``},
        delimiter: {type: String, default: `,`},
        joinValues: {type: Boolean, default: false},
        zIndex: {type: [Number, String], default: 999},
        maxHeight: {type: Number, default: 300},
        noOptionsText: {type: String, default: `No options available.`},
        noResultsText: {type: String, default: `No results found...`},
        loadingText: {type: String, default: `Loading...`},
        searchPrompt: {type: String, default: `Type to search...`},
        defaultExpandLevel: {type: Number, default: 0},
        instanceId: {
            type: [String, Number],
            default() {
                return `treeselect-` + Math.floor(Math.random() * 10000)
            }
        },
        tabIndex: {type: Number, default: 0},
        theme: {type: String, default: `auto`},
        variant: {type: String, default: `success`}
    },
    data() {
        return {
            menuIsOpen: false,
            searchQuery: ``,
            internalOptions: [],
            rootOptions: [],
            expandedState: [],
            menuLoading: false,
            scrollLoading: false,
            highlightedNodeId: null,
            selectedNodes: [],
            // Virtual scrolling state on async mode
            currentPage: 1,
            limit: 20,
            hasMore: true,
            scrollTop: 0,
            itemHeight: 24
        }
    },
    computed: {
        visibleCount() {
            if (this.async || !this.virtualScroll) return this.filteredRootOptions.length;
            return Math.ceil(this.maxHeight / this.itemHeight);
        },
        visibleStartIndex() {
            if (this.async || !this.virtualScroll) return 0;
            // Buffer of 5 items before the visible region
            return Math.max(0, Math.floor(this.scrollTop / this.itemHeight) - 5);
        },
        visibleEndIndex() {
            if (this.async || !this.virtualScroll) return this.filteredRootOptions.length;
            return Math.min(this.filteredRootOptions.length, Math.floor(this.scrollTop / this.itemHeight) + this.visibleCount + 5);
        },
        visibleRootOptions() {
            return this.filteredRootOptions.slice(this.visibleStartIndex, this.visibleEndIndex);
        },
        topSpacerHeight() {
            if (this.async || !this.virtualScroll) return 0;
            return this.visibleStartIndex * this.itemHeight;
        },
        bottomSpacerHeight() {
            if (this.async || !this.virtualScroll) return 0;
            return (this.filteredRootOptions.length - this.visibleEndIndex) * this.itemHeight;
        },
        checkedState() {
            const selectedSet = new Set(this.selectedNodes.map(n => String(n[this.trackBy])))
            const map = this.buildCheckedStates(this.rootOptions, selectedSet, this.valueConsistsOf)
            const checkedIds = []
            const indeterminateIds = []
            for (const [nodeId, state] of Object.entries(map)) {
                if (state.checked) checkedIds.push(nodeId)
                if (state.indeterminate) indeterminateIds.push(nodeId)
            }
            return {
                checkedIds,
                indeterminateIds
            }
        },
        joinedValue() {
            if (!this.selectedNodes.length) return ``
            const arr = this.selectedNodes.map(n => n[this.trackBy]);
            if (!this.multiple) {
                return this.joinValues ? String(arr[0] || ``) : JSON.stringify(arr[0] || null)
            } else {
                return this.joinValues ? arr.join(this.delimiter) : JSON.stringify(arr)
            }
        },
        computedTabIndex() {
            return this.disabled ? -1 : this.tabIndex
        },
        filteredData() {
            if (!this.searchQuery) {
                return { filtered: this.rootOptions, expandedIds: this.getDefaultExpandedIds() };
            }
            const lowerQ = this.searchQuery.toLowerCase();
            const expandedIds = new Set();

            const filterNodes = (nodes) => {
                return nodes
                    .map((node) => {
                        let matches = false;
                        const labelLower = node[this.label].toLowerCase();
                        if (this.disableFuzzyMatching) {
                            matches = labelLower.includes(lowerQ);
                        } else {
                            matches = this.simpleFuzzy(labelLower, lowerQ);
                        }

                        if (Array.isArray(node.children) && node.children.length) {
                            const filteredChildren = filterNodes(node.children);
                            if (filteredChildren.length > 0) {
                                expandedIds.add(node[this.trackBy]);
                                return { ...node, children: filteredChildren };
                            }
                        }
                        return matches ? node : null;
                    })
                    .filter(Boolean);
            };

            const filtered = filterNodes(this.rootOptions);

            return { filtered, expandedIds: Array.from(expandedIds) };
        },
        filteredRootOptions() {
            return this.filteredData.filtered;
        }
    },
    watch: {
        options: {
            immediate: true,
            handler(newVal) {
                this.internalOptions = structuredClone(newVal)
                this.rootOptions = this.internalOptions.filter((n) => n.parentId == null)
                this.setDefaultExpandedState()
            }
        },
        value: {
            immediate: true,
            handler(val) {
                if (this.multiple && Array.isArray(val)) {
                    this.selectedNodes = this.getNodesFromValueArray(val)
                } else if (!this.multiple && val != null && val !== ``) {
                    this.selectedNodes = this.getNodesFromValueArray([val])
                } else {
                    this.selectedNodes = []
                }
            }
        },
        searchQuery(searchVal) {
            this.$emit(`search-change`, this.searchQuery, this.instanceId)
            if (this.async && this.loadData) {
                this.triggerAsyncSearch(this.searchQuery)
            }
            if (!searchVal) {
                this.setDefaultExpandedState();
            }
        },
        filteredData(newVal) {
            this.expandedState = newVal.expandedIds;
        },
        menuIsOpen(newVal) {
            this.$nextTick(() => {
                const menu = this.$refs.menu
                const scrollCallback = this.async ? this.handleScroll : this.onVirtualScroll
                if (menu) {
                    if (newVal) {
                        menu.addEventListener(`scroll`, scrollCallback)
                    } else {
                        menu.removeEventListener(`scroll`, scrollCallback)
                    }
                }
            })
        }
    },
    methods: {
        emitEvent(eventName, ...args) {
            this.$emit(eventName, ...args, this.instanceId)
        },
        openMenu() {
            if (this.menuIsOpen) return
            this.menuIsOpen = true
            this.emitEvent(`open`)
        },
        closeMenu() {
            if (!this.menuIsOpen) return
            this.menuIsOpen = false
            this.searchQuery = ``
            this.emitEvent(`close`, this.value)
        },
        handleCaretClick(e) {
            if (this.disabled) return;
            this.menuIsOpen = !this.menuIsOpen;
            this.$refs.searchInput.focus();
        },
        handleFocusIn(e) {
            if (this.disabled) return;
            this.$refs.searchInput.focus();
            if (!this.menuIsOpen) this.menuIsOpen = true;
        },
        handleFocusOut(e) {
            if (this.disabled) return
            if (!this.$el.contains(e.relatedTarget)) {
                this.menuIsOpen = false
                this.searchQuery = ``
            }
        },
        handleKeydown(e) {
            if (e.key === `Escape`) {
                e.preventDefault()
                this.closeMenu()
            }
        },
        emitSelectedNodes() {
            this.$emit(`input`, this.selectedNodes, this.instanceId)
        },
        clearAll() {
            this.selectedNodes = []
            this.$emit(`input`, this.multiple ? [] : null, this.instanceId)
        },
        clearValue() {
            this.selectedNodes = []
            this.$emit(`input`, this.multiple ? [] : null, this.instanceId)
        },
        uncheckSingleNode(node) {
            if (!this.multiple) {
                this.clearValue()
                return
            }
            this.selectedNodes = this.selectedNodes.filter(n => n[this.trackBy] !== node[this.trackBy])
            this.emitSelectedNodes();
        },
        handleCheck(node) {
            const isIndeterminate = this.checkedState.indeterminateIds.includes(node[this.trackBy])
            if (this.multiple && isIndeterminate) {
                const descIds = this.getAllDescendantIds(node)
                this.selectedNodes = this.selectedNodes.filter(n => !descIds.includes(n[this.trackBy]) && n[this.trackBy] !== node[this.trackBy])
                this.emitSelectedNodes();
                return
            }
            const hasNode = this.selectedNodes.some(n => n[this.trackBy] === node[this.trackBy])
            if (!this.multiple) {
                this.selectedNodes = hasNode ? [] : [node]
                this.$emit(`input`, this.selectedNodes[0] || null, this.instanceId)
                if (!hasNode && this.closeOnSelect) this.closeMenu()
                return
            }
            if (!hasNode) {
                if (!node.children || !node.children.length) {
                    this.selectedNodes.push(node)
                } else {
                    if (this.valueConsistsOf === `BRANCH_PRIORITY`) {
                        const childIds = this.getAllDescendantIds(node)
                        this.selectedNodes = this.selectedNodes.filter(n => !childIds.includes(n[this.trackBy]))
                        if (!this.selectedNodes.some(n => n[this.trackBy] === node[this.trackBy])) this.selectedNodes.push(node)
                    } else if (this.valueConsistsOf === `ALL`) {
                        const subtreeIds = [node[this.trackBy], ...this.getAllDescendantIds(node)]
                        subtreeIds.forEach(id => {
                            const existing = this.selectedNodes.find(n => n[this.trackBy] === id)
                            if (!existing) {
                                const found = this.getNodeById(id)
                                if (found) this.selectedNodes.push(found)
                            }
                        })
                    } else if (this.valueConsistsOf === `LEAF_PRIORITY`) {
                        // Remove any branch IDs in the subtree
                        const branchIds = this.getAllDescendantBranchIds(node)
                        this.selectedNodes = this.selectedNodes.filter(n => !branchIds.includes(n[this.trackBy]))

                        // Add all leaf IDs
                        const leafIds = this.getAllDescendantLeafIds(node)
                        leafIds.forEach(id => {
                            if (!this.selectedNodes.find(n => n[this.trackBy] === id)) {
                                const found = this.getNodeById(id)
                                if (found) this.selectedNodes.push(found)
                            }
                        })
                    } else if (this.valueConsistsOf === `ALL_WITH_INDETERMINATE`) {
                        const subtreeIds = [node[this.trackBy], ...this.getAllDescendantIds(node)]
                        subtreeIds.forEach(id => {
                            const existing = this.selectedNodes.find(n => n[this.trackBy] === id)
                            if (!existing) {
                                const found = this.getNodeById(id)
                                if (found) this.selectedNodes.push(found)
                            }
                        })
                    }
                }
                this.emitEvent(`select`, node)
            } else {
                if (!node.children || !node.children.length) {
                    this.selectedNodes = this.selectedNodes.filter(n => n[this.trackBy] !== node[this.trackBy])
                } else {
                    if (this.valueConsistsOf === `BRANCH_PRIORITY`) {
                        this.selectedNodes = this.selectedNodes.filter(n => n[this.trackBy] !== node[this.trackBy])
                    } else if (this.valueConsistsOf === `ALL` || this.valueConsistsOf === `ALL_WITH_INDETERMINATE`) {
                        const subtreeIds = [node[this.trackBy], ...this.getAllDescendantIds(node)]
                        this.selectedNodes = this.selectedNodes.filter(n => !subtreeIds.includes(n[this.trackBy]))
                    } else if (this.valueConsistsOf === `LEAF_PRIORITY`) {
                        const leafIds = this.getAllDescendantLeafIds(node)
                        this.selectedNodes = this.selectedNodes.filter(n => !leafIds.includes(n[this.trackBy]))
                    }
                }
                this.emitEvent(`deselect`, node)
            }
            if (this.closeOnSelect && !this.flat) {
                this.closeMenu()
            }
            this.emitSelectedNodes();
        },
        toggleExpanded(nodeId, shouldExpand) {
            const arr = [...this.expandedState]
            const i = arr.indexOf(nodeId)
            if (shouldExpand && i < 0) arr.push(nodeId)
            if (!shouldExpand && i > -1) arr.splice(i, 1)
            this.expandedState = arr
        },
        getDefaultExpandedIds() {
            const expanded = [];
            const traverse = (node, level) => {
                if (level < this.defaultExpandLevel) {
                    if (node.children && node.children.length) {
                        expanded.push(node[this.trackBy]);
                    }
                    if (node.children && node.children.length) {
                        node.children.forEach(child => traverse(child, level + 1));
                    }
                }
            };
            this.rootOptions.forEach(root => traverse(root, 0));
            return expanded;
        },
        setDefaultExpandedState() {
            if (this.defaultExpandLevel < 1) return;
            this.expandedState = this.getDefaultExpandedIds();
        },
        async handleLoadChildren(node) {
            if (!this.loadData) return
            this.menuLoading = true
            try {
                const childrenArray = await this.loadData({
                    action: `LOAD_CHILDREN_OPTIONS`,
                    parentNode: node,
                    instanceId: this.instanceId
                })
                if (Array.isArray(childrenArray)) {
                    this.$set(node, `children`, childrenArray)
                }
            } catch (err) {
                console.error(`Error loading children:`, err)
            } finally {
                this.menuLoading = false
            }
        },
        async triggerAsyncSearch(query) {
            if (!this.fetchItems || !query) return;
            this.menuLoading = true;
            try {
                await this.loadData({action: `ASYNC_SEARCH`, searchQuery: query});
            } catch (err) {
                console.error(`Error during async search:`, err);
            } finally {
                this.menuLoading = false;
            }
        },
        async loadData({action, searchQuery}) {
            if (action === `ASYNC_SEARCH`) {
                // New search: reset pagination
                this.currentPage = 1;
                this.hasMore = true;
                const pagination = {
                    limit: this.limit,
                    page: 1,
                    offset: 0,
                    search: searchQuery
                };
                const result = await this.fetchItems(pagination);
                const newOptions = result.results;
                this.internalOptions = structuredClone(newOptions);
                this.rootOptions = this.internalOptions.filter(n => n.parentId == null);
            } else if (action === `LOAD_MORE`) {
                if (!this.hasMore) return;
                this.currentPage++;
                const pagination = {
                    limit: this.limit,
                    page: this.currentPage,
                    offset: (this.currentPage - 1) * this.limit,
                    search: searchQuery
                };
                const result = await this.fetchItems(pagination);
                const moreOptions = result.results;
                this.internalOptions = [...this.internalOptions, ...moreOptions];
                this.rootOptions = this.internalOptions.filter(n => n.parentId == null);
                if (this.internalOptions.length >= result.count) {
                    this.hasMore = false;
                }
            }
        },
        async handleScroll(e) {
            const menu = e.target;
            if (!menu || !this.async) return;
            const scrollTop = menu.scrollTop;
            const scrollHeight = menu.scrollHeight;
            const clientHeight = menu.clientHeight;
            if (scrollTop + clientHeight >= scrollHeight - 10) {
                if (!this.menuLoading && this.hasMore) {
                    this.menuLoading = true;
                    this.scrollLoading = true;
                    try {
                        await this.loadData({action: `LOAD_MORE`, searchQuery: this.searchQuery});
                    } catch (err) {
                        console.error(`Error loading more options:`, err);
                    } finally {
                        this.menuLoading = false;
                        this.scrollLoading = false;
                    }
                }
            }
        },
        onVirtualScroll(e) {
            if (this._scrolling || this.async) return;
            this._scrolling = true;
            requestAnimationFrame(() => {
                this.scrollTop = e.target.scrollTop;
                this._scrolling = false;
            });
        },
        getNodeById(id, nodes = null) {
            if (!nodes) nodes = this.internalOptions
            for (const n of nodes) {
                if (n[this.trackBy] === id) return n
                if (Array.isArray(n.children) && n.children.length) {
                    const childMatch = this.getNodeById(id, n.children)
                    if (childMatch) return childMatch
                }
            }
            return null
        },
        getAllDescendantIds(node) {
            const ids = [node[this.trackBy]]
            if (Array.isArray(node.children)) {
                node.children.forEach((child) => {
                    ids.push(...this.getAllDescendantIds(child))
                })
            }
            return ids
        },
        getAllDescendantLeafIds(node) {
            if (!node.children || !node.children.length) {
                return [node[this.trackBy]]
            }
            let r = []
            node.children.forEach((c) => {
                r = r.concat(this.getAllDescendantLeafIds(c))
            })
            return r
        },
        getAllDescendantBranchIds(node) {
            const result = []
            if (node.children && node.children.length) {
                result.push(node[this.trackBy])
                node.children.forEach((child) => {
                    if (child.children && child.children.length) {
                        result.push(...this.getAllDescendantBranchIds(child))
                    }
                })
            }
            return result
        },
        simpleFuzzy(str, q) {
            let j = 0;
            for (let i = 0; i < str.length && j < q.length; i++) {
                if (str[i] === q[j]) j++;
            }
            return j === q.length;
        },
        buildCheckedStates(roots, selectedSet, valueConsistsOf) {
            const nodeStates = {}

            const dfs = (node) => {
                const nodeId = node[this.trackBy]
                const isLeaf = !node.children || !node.children.length

                // ----- 1) LEAF CASE -----
                if (isLeaf) {
                    const isChecked = selectedSet.has(nodeId)
                    nodeStates[nodeId] = {
                        checked: isChecked,
                        indeterminate: false
                    }
                    return {total: 1, checked: isChecked ? 1 : 0}
                }

                // Branch node => recurse
                let totalLeaves = 0
                let checkedLeaves = 0
                for (const child of node.children) {
                    const childRes = dfs(child)
                    totalLeaves += childRes.total
                    checkedLeaves += childRes.checked
                }

                // Now apply the modes
                const branchIsInSet = selectedSet.has(nodeId)

                if (valueConsistsOf === `ALL`) {
                    // Node is fully checked if:
                    //   - The branch itself is in the set, OR
                    //   - All child leaves are checked (checkedLeaves === totalLeaves)
                    const nodeChecked = branchIsInSet || (checkedLeaves === totalLeaves && totalLeaves > 0)
                    const nodeIndeterminate = !nodeChecked && checkedLeaves > 0
                    nodeStates[nodeId] = {
                        checked: nodeChecked,
                        indeterminate: nodeIndeterminate
                    }
                    return {
                        total: totalLeaves,
                        checked: nodeChecked ? totalLeaves : checkedLeaves
                    }
                }

                if (valueConsistsOf === `ALL_WITH_INDETERMINATE`) {
                    // Similar to ALL, but “selectedSet.has(nodeId)” automatically means
                    // node is considered checked even if none of its children are in the set
                    const nodeChecked = branchIsInSet || checkedLeaves > 0
                    const nodeIndeterminate =
                        nodeChecked && !branchIsInSet && checkedLeaves < totalLeaves
                    nodeStates[nodeId] = {
                        checked: nodeChecked,
                        indeterminate: nodeIndeterminate
                    }
                    // If the branch is considered checked, treat all children as effectively checked
                    return {total: totalLeaves, checked: nodeChecked ? totalLeaves : 0}
                }

                if (valueConsistsOf === `BRANCH_PRIORITY`) {
                    // If the branch is explicitly in the set => overshadow the children
                    if (branchIsInSet) {
                        nodeStates[nodeId] = {checked: true, indeterminate: false}
                        return {total: totalLeaves, checked: 1}
                    } else {
                        // Otherwise, if some children are checked => indeterminate
                        // if all are checked => checked
                        const allChildrenChecked = (checkedLeaves === totalLeaves && totalLeaves > 0)
                        const someChildrenChecked = (checkedLeaves > 0)
                        nodeStates[nodeId] = {
                            checked: allChildrenChecked,
                            indeterminate: !allChildrenChecked && someChildrenChecked
                        }
                        // NOTE: returning (checked: checkedLeaves) means up the chain
                        // sees how many *leaf* children are selected
                        return {total: totalLeaves, checked: checkedLeaves}
                    }
                }

                if (valueConsistsOf === `LEAF_PRIORITY`) {
                    // In leaf priority, we do NOT want to store this branch's id
                    // in the selectedSet. So if the user “selects” a branch, we
                    // remove branch[this.trackBy] and add the leaf ids instead (done in handleCheck).
                    // Then in buildCheckedStates, branches are never truly “checked”:
                    // - checked = false
                    // - indeterminate = true if ANY of its leaves are checked
                    const someLeavesChecked = checkedLeaves > 0
                    nodeStates[nodeId] = {
                        checked: false,
                        indeterminate: someLeavesChecked
                    }
                    // Return how many leaves are actually checked (so that
                    // ancestors know how many leaves in their subtrees are checked)
                    return {total: totalLeaves, checked: checkedLeaves}
                }
            }

            // Run DFS on each root
            roots.forEach(r => dfs(r))
            return nodeStates
        },
        getNodesFromValueArray(arr) {
            const results = []
            arr.forEach(val => {
                const node = this.findNodeById(val[this.trackBy])
                if (node) results.push(node)
            })
            return results
        },
        findNodeById(id, nodes = null) {
            if (!nodes) {
                nodes = this.internalOptions;
            }

            for (const node of nodes) {
                if (node[this.trackBy] === id) {
                    return node;
                }

                if (node.children && node.children.length) {
                    const childNode = this.findNodeById(id, node.children);
                    if (childNode) {
                        return childNode;
                    }
                }
            }

            return null;
        }
    }
}
</script>

<style scoped>
.treeselect {
    position: relative;
    font-family: inherit;
    min-width: 100px;
    width: 100%;
}

.theme-light {
    --app-bg-color: #fafafa;
    --input-bg-color: rgba(255, 255, 255, 0.1);
    --input-group-text-bg-color: #e9ecef;
    --imput-border-color: rgba(0, 0, 0, 0.2);
    --text-hight-contrast: #111111;
    --text-low-contrast: #495057;
    --text-solor: #605e74;
    --btn-simple-bg: #eee;

    ::-webkit-scrollbar-thumb {
        background-color: var(--text-low-contrast) ;
        border-radius: 6px;
        border: none;
    }
}

.theme-dark {
    --input-group-text-bg-color: var(--input-bg-color);
}

.treeselect-prepend-group {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    min-width: 100px;

    .treeselect-control {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
        width: 100%;
    }

    & > .input-group-prepend {
        width: 46px;
        gap: 0;

        .input-group-text {
            height: 100%;
            width: 100%;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-color: var(--input-border-color);
            background-color: var(--input-group-text-bg-color);
            color: var(--text-low-contrast);
        }
    }
}

.treeselect--disabled .treeselect-control {
    background-color: #f0f0f0;
    cursor: not-allowed;
}

.form-control:disabled {
    border: none;
}

.treeselect-control {
    border: 1px solid;
    border-color: var(--input-border-color);
    min-height: 34px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    position: relative;
    background-color: var(--input-bg-color);
}

.treeselect-control:focus-within {
    box-shadow: 0 0 0 0.2rem rgba(21, 143, 195, 0.25);
    outline: none;
}

.input-group-prepend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 120px;
    overflow: auto;
}

.treeselect-value-pill {
    display: flex;
    align-items: center;
    line-height: 1;
    height: fit-content;
    font-size: 10pt;
    font-family: "segoe ui", roboto, sans-serif;
    font-weight: 500;
    color: white;
    border-radius: 3px;
    padding-left: 8px;
    background: var(--color-light);
    --pill-border: rgba(255, 255, 255, 0.5);

    &.success {
        background: var(--color-success);
    }

    &.primary {
        background: var(--color-primary);
    }

    &.accent {
        background: var(--color-accent);
    }

    &.simple {
        --pill-border: rgba(60,60,60,0.26);
        background: #f0f0f0;
        border: 1px solid var(--pill-border);
        color: #333;
    }

    &.all-selected {
        background: var(--btn-simple-bg);
        color: var(--text-color);
        border: 1px solid var(--border-subtle-color);
        text-decoration: none !important;
    }
}

.treeselect-value-pill-label {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 200px;
}

.treeselect-value-pill-remove,
.treeselect-value-single-remove {
    all: unset;
    cursor: pointer;
    border-left: 1px solid var(--pill-border);
    padding: 4px;
    margin-left: 4px;

    &:hover {
        background-color: rgba(0, 0, 0, 0.1);
    }

    &:focus {
        outline: revert;
    }
}

.treeselect-search-input-wrapper {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 4px;
}

.treeselect-search-input {
    border: none;
    outline: none;
    box-shadow: none;
    flex: 1;
    min-width: 50px;
    background-color: transparent !important;
}

.treeselect-search-input--fake {
    flex: 1;
}

.treeselect-clear-all {
    all: unset;
    display: flex;
    align-items: center;
    margin-left: auto;
    padding: 4px;
    cursor: pointer;
    font-size: 14px;
    opacity: 0.5;

    &:focus {
        outline: revert;
    }
}

.treeselect-clear-all:hover {
    opacity: 1;
    color: red;
}

.treeselect-arrow {
    all: unset;
    display: flex;
    align-items: center;
    padding: 4px;
    cursor: pointer;
    opacity: 0.5;

    &:focus {
        outline: revert;
    }
}

.treeselect-arrow:hover {
    opacity: 1;
}

.treeselect--open .treeselect-menu-container {
    display: block;
}

.treeselect--always-open {
    .treeselect-menu-container {
        position: initial;
        box-shadow: none;
        z-index: 0 !important;
        max-height: 600px !important;
    }
}

.treeselect-menu-container {
    position: absolute;
    top: 100%;
    left: 0;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-top: none;
    width: 100%;
    max-height: 300px;
    overflow: auto;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    background: var(--app-bg-color);
}

.treeselect-menu {
    list-style: none;
    margin: 0;
    padding: 4px 0;
}

.treeselect-before-list,
.treeselect-after-list {
    padding: 4px;
}

.treeselect-loading,
.treeselect-no-options,
.treeselect-no-results {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    text-align: center;
    opacity: 0.5;
}

.invisible {
    visibility: hidden;
}
</style>
